import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FolderOpen, ClipboardList } from "lucide-react";
import SavedPapers from "./SavedPapers";
import MySubmissions from "./MySubmissions";

export default function MyPapers() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const initial = params.get("tab") === "completed" ? "completed" : "generated";
  const [tab, setTab] = useState(initial);

  // Keep the URL in sync so a page refresh preserves the tab and old
  // redirects (?tab=completed) drop the user in the right place.
  useEffect(() => {
    const current = new URLSearchParams(location.search).get("tab");
    const wanted = tab === "completed" ? "completed" : null;
    if (current !== wanted) {
      const next = wanted ? `?tab=${wanted}` : "";
      navigate({ pathname: "/my-papers", search: next }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="my-papers-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Library</p>
      <h1 className="text-4xl font-bold tracking-tighter">My Papers</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-xl">Everything you've generated and everything you've sat, in one place.</p>

      {/* Tab strip — same visual language as the Generate page's mode selector */}
      <div className="mt-8 max-w-md">
        <div role="tablist" className="grid grid-cols-2 gap-2 rounded-full bg-muted/40 p-1" data-testid="my-papers-tabs">
          <button role="tab" aria-selected={tab === "generated"} data-testid="my-papers-tab-generated"
            onClick={() => setTab("generated")}
            className={`h-9 rounded-full text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${tab === "generated" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
            <FolderOpen className="h-3.5 w-3.5" /> Generated
          </button>
          <button role="tab" aria-selected={tab === "completed"} data-testid="my-papers-tab-completed"
            onClick={() => setTab("completed")}
            className={`h-9 rounded-full text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${tab === "completed" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
            <ClipboardList className="h-3.5 w-3.5" /> Completed
          </button>
        </div>
      </div>

      <div className="mt-8">
        {tab === "generated" && (
          <div data-testid="my-papers-generated-panel">
            <SavedPapers embedded />
          </div>
        )}
        {tab === "completed" && (
          <div data-testid="my-papers-completed-panel">
            <MySubmissions embedded onSwitchToGenerated={() => setTab("generated")} />
          </div>
        )}
      </div>
    </div>
  );
}
